import { EChart } from "@/lib/EChart";
import type { Infringement } from "@/types/domain";
import { useThemeColors } from "@/hooks/useThemeColors";
import { useFilteredInfringements } from "@/hooks/useFilteredInfringements";
import { WidgetShell } from "@/components/dashboard/WidgetShell";

function DetectionResponseTimeline({ infringements }: { infringements: Infringement[] }) {
  const colors = useThemeColors();

  const intervals = new Map<string, { detections: number }>();
  infringements.forEach((inf) => {
    const date = new Date(inf.detected);
    const rounded = new Date(Math.floor(date.getTime() / (15 * 60 * 1000)) * (15 * 60 * 1000));
    const key = rounded.toISOString();
    if (!intervals.has(key)) intervals.set(key, { detections: 0 });
    const entry = intervals.get(key)!;
    entry.detections++;
  });

  const sortedKeys = Array.from(intervals.keys()).sort();
  const timestamps = sortedKeys.map((k) =>
    new Date(k).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
  );
  const detections = sortedKeys.map((k) => intervals.get(k)!.detections);

  const option = {
    backgroundColor: "transparent",
    grid: { left: "8%", right: "8%", top: "12%", bottom: "12%", containLabel: true },
    tooltip: {
      trigger: "axis",
      backgroundColor: colors.surfaceElevated,
      borderColor: colors.border,
      borderWidth: 1,
      textStyle: { color: colors.textPrimary, fontSize: 12 },
      formatter: (params: any) => {
        let content = `<div style="font-weight:600;margin-bottom:6px;font-size:11px;">${params[0].axisValue}</div>`;
        params.forEach((item: any) => {
          content += `<div style="display:flex;align-items:center;gap:8px;margin-top:4px;">
            <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${item.color}"></span>
            <span style="flex:1">${item.seriesName}</span>
            <span style="font-weight:600">${item.value}</span>
          </div>`;
        });
        return content;
      },
    },
    legend: {
      data: ["Detections"],
      top: 0,
      left: "center",
      textStyle: { color: colors.textSecondary, fontSize: 11, fontFamily: "Inter, sans-serif" },
      itemWidth: 16,
      itemHeight: 10,
      icon: "circle",
    },
    xAxis: {
      type: "category",
      data: timestamps,
      boundaryGap: false,
      axisLine: { lineStyle: { color: colors.border } },
      axisTick: { show: false },
      axisLabel: {
        color: colors.textMuted,
        fontSize: 10,
        fontFamily: "JetBrains Mono, monospace",
        interval: Math.floor(timestamps.length / 6),
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      splitLine: { lineStyle: { color: colors.border, width: 1 } },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: colors.textMuted, fontSize: 11, fontFamily: "Inter, sans-serif" },
    },
    series: [
      {
        name: "Detections",
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 4,
        lineStyle: { color: colors.brandCyan, width: 2 },
        itemStyle: { color: colors.brandCyan, borderWidth: 2, borderColor: colors.brandCyan },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "rgba(6,182,212,0.15)" },
              { offset: 1, color: "rgba(6,182,212,0.01)" },
            ],
          },
        },
        data: detections,
      },
    ],
  };

  return (
    <div className="h-full">
      {infringements.length === 0 ? (
        <div className="flex items-center justify-center h-full text-sm text-muted">
          No data available
        </div>
      ) : (
        <EChart option={option} style={{ height: "100%" }} />
      )}
    </div>
  );
}

export function DetectionTimelineWidget() {
  const { infringements } = useFilteredInfringements();
  return (
    <WidgetShell title="Detection &amp; Response">
      <div className="flex-1 min-h-0 px-4 pb-2">
        <DetectionResponseTimeline infringements={infringements} />
      </div>
    </WidgetShell>
  );
}
